import { MetaApiResponse } from '@/utils/types/CommonApiResponse';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { queryClient } from 'providers/AllProviders';
import {
  assignTelephonyEndpointRoles,
  createTelephonyEndpoint,
  deleteTelephonyEndpoint,
  getAvailableEndpointNumbers,
  getTelephonyEndpoint,
  getTelephonyEndpointGatewayState,
  getTelephonyEndpoints,
  purchaseTelephonyEndpoint,
  updateTelephonyEndpoint
} from './api';
import { AssignRolesPayload, CreateSipEndpointPayload, PurchaseEndpointPayload, TelephonyEndpoint } from './types';

type EndpointsQueryData = { data: TelephonyEndpoint[]; meta: MetaApiResponse };
type EndpointsQueryOptions = Omit<
  UseQueryOptions<EndpointsQueryData, Error, EndpointsQueryData, readonly unknown[]>,
  'queryKey' | 'queryFn'
>;

const invalidateEndpoints = () => {
  queryClient.invalidateQueries({ queryKey: ['telephony_endpoints'] });
};

export function useGetTelephonyEndpoints(params?: Record<string, unknown>, options?: EndpointsQueryOptions) {
  return useQuery<EndpointsQueryData>({
    queryKey: ['telephony_endpoints', params],
    queryFn: () => getTelephonyEndpoints(params),
    ...options
  });
}

export function useGetTelephonyEndpoint(
  id?: number | string | null,
  options?: Omit<
    UseQueryOptions<TelephonyEndpoint, Error, TelephonyEndpoint, readonly unknown[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<TelephonyEndpoint>({
    queryKey: ['telephony_endpoints', 'detail', id],
    queryFn: () => getTelephonyEndpoint(id as number | string),
    enabled: Boolean(id),
    ...options
  });
}

export function useCreateTelephonyEndpoint() {
  return useMutation({
    mutationFn: (payload: CreateSipEndpointPayload) => createTelephonyEndpoint(payload),
    onSuccess: invalidateEndpoints
  });
}

export function useUpdateTelephonyEndpoint() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: { telephony_endpoint: Record<string, unknown> } }) =>
      updateTelephonyEndpoint(id, payload),
    onSuccess: invalidateEndpoints
  });
}

export function useDeleteTelephonyEndpoint() {
  return useMutation({
    mutationFn: (id: number | string) => deleteTelephonyEndpoint(id),
    onSuccess: invalidateEndpoints
  });
}

export function usePurchaseTelephonyEndpoint() {
  return useMutation({
    mutationFn: (payload: PurchaseEndpointPayload) => purchaseTelephonyEndpoint(payload),
    onSuccess: invalidateEndpoints
  });
}

export function useAssignTelephonyEndpointRoles() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: AssignRolesPayload }) =>
      assignTelephonyEndpointRoles(id, payload),
    onSuccess: invalidateEndpoints
  });
}

export function useGetAvailableEndpointNumbers(
  params: Record<string, unknown> | null,
  options?: Omit<
    UseQueryOptions<{ data: string[] }, Error, { data: string[] }, readonly unknown[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<{ data: string[] }>({
    queryKey: ['telephony_endpoints', 'available_numbers', params],
    queryFn: () => getAvailableEndpointNumbers(params as Record<string, unknown>),
    enabled: Boolean(params),
    ...options
  });
}

export function useGetTelephonyEndpointGatewayState(
  id?: number | string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) {
  return useQuery({
    queryKey: ['telephony_endpoints', 'gateway_state', id],
    queryFn: () => getTelephonyEndpointGatewayState(id as number | string),
    enabled: Boolean(id) && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
    staleTime: 0
  });
}
